/*
* Copiado de @vuetify/vuex-cognito-module con algunos agregados propios.
* Adaptado a AWS Amplify v6 para mitigar el fin de ciclo de vida (EOL).
* */

import { Amplify } from 'aws-amplify';
import { 
    fetchAuthSession, 
    getCurrentUser, 
    signIn, 
    confirmSignIn,
    signUp, 
    confirmSignUp, 
    resendSignUp, 
    resetPassword, 
    confirmResetPassword, 
    signOut, 
} from 'aws-amplify/auth';

const state = () => ({
    session: {},
    user: {},
});

const mutations = {
    setUser: (state, user) => {
        state.user = { ...user };
        // En v6, el manejo de la sesión se hace mediante fetchAuthSession, 
        // pero mapeamos los datos aquí para mantener compatibilidad con tus getters existentes.
        state.session = user.session || {};
    },
};

const getters = {
    isLoggedIn: (store = {}) => {
        const session = store.session;
        if (!session || !session.tokens) return false;
        const accessToken = session.tokens.accessToken;
        if (!accessToken) return false;
        
        // v6 expone directamente los tokens decodificados
        const isActive = new Date(accessToken.payload.exp * 1000) > new Date();
        const isMe = store.user && (store.user.username === accessToken.payload.username);
        return isActive && isMe;
    },
    session: (store = {}) =>
        'session' in store && Object.keys(store.session).length !== 0
            ? store.session
            : false,
    userSub: (store = {}) => {
        if (store.user && store.user.userId) {
            return store.user.userId; // v6 usa userId en lugar de attributes.sub para el identificador único
        }
        return false;
    },
    username: (store = {}) => store.user?.username ?? false,
    userAttributes: (store = {}) =>
        store.user && store.user.attributes
            ? store.user.attributes
            : false,
    userGroups: (store = {}) => {
        if (store.session && store.session.tokens && store.session.tokens.accessToken) {
            return store.session.tokens.accessToken.payload['cognito:groups'] ?? false;
        }
        return false;
    },
};

const actions = {
    fetchSession: ({ commit }) =>
        new Promise((resolve, reject) => {
            fetchAuthSession().then(session => {
                getCurrentUser()
                    .then(user => {
                        // Adjuntamos la sesión al usuario para mantener la mutación limpia
                        user.session = session;
                        commit('setUser', user);
                        resolve(session);
                    }).catch(err => {
                        // Si no hay usuario activo, limpiamos el estado silenciosamente o arrojamos el error
                        commit('setUser', {});
                        reject(err);
                    });
            }).catch(reject);
        }),
    fetchJwtToken: () =>
        new Promise((resolve, reject) => {
            fetchAuthSession().then(session => {
                if (session.tokens && session.tokens.accessToken) {
                    resolve(session.tokens.accessToken.toString());
                } else {
                    reject(new Error('No active tokens found'));
                }
            }).catch(reject);
        }),
    signInUser: ({ commit }, credentials) =>
        new Promise((resolve, reject) => {
            signIn({ username: credentials.username, password: credentials.password }).then((output) => {
                // En v6 signIn retorna un objeto con el estado del paso (nextStep)
                if (output.isSignedIn) {
                    getCurrentUser().then(user => {
                        fetchAuthSession().then(session => {
                            user.session = session;
                            commit('setUser', user);
                            resolve(user);
                        });
                    }).catch(reject);
                } else {
                    // Si requiere MFA o cambio de contraseña (NUEVO CONTROL)
                    resolve(output);
                }
            }).catch(reject);
        }),
    answerCustomChallenge: ({ commit }, credentials) =>
        new Promise((resolve, reject) => {
            confirmSignIn({ challengeResponse: credentials.answer }).then((output) => {
                if (output.isSignedIn) {
                    getCurrentUser().then(user => {
                        fetchAuthSession().then(session => {
                            user.session = session;
                            commit('setUser', user);
                            resolve(user);
                        });
                    }).catch(reject);
                } else {
                    resolve(output);
                }
            }).catch(reject);
        }),
    registerUser: ({ commit }, credentials) =>
        new Promise((resolve, reject) => {
            signUp({
                username: credentials.username,
                password: credentials.password,
                options: {
                    userAttributes: credentials.attributes, // En v6 va dentro de options
                },
            }).then(output => {
                resolve(output);
            }).catch(reject);
        }),
    confirmUser: (_, data) =>
        new Promise((resolve, reject) => {
            confirmSignUp({ username: data.username, confirmationCode: data.code })
                .then(resolve)
                .catch(reject);
        }),
    resendConfirmation: (_, data) =>
        new Promise((resolve, reject) => {
            resendSignUp({ username: data.username })
                .then(resolve)
                .catch(reject);
        }),
    forgotPassword: (_, data) =>
        new Promise((resolve, reject) => {
            resetPassword({ username: data.username })
                .then(resolve)
                .catch(reject);
        }),
    changePassword: (_, data) =>
        new Promise((resolve, reject) => {
            confirmResetPassword({ username: data.username, confirmationCode: data.code, newPassword: data.newPassword })
                .then(resolve)
                .catch(reject);
        }),
    signOut: ({ commit, getters }) =>
        new Promise((resolve, reject) => {
            if (!getters.isLoggedIn) {
                reject(new Error('User not logged in.'));
                return;
            }
            signOut()
                .then(result => {
                    commit('setUser', {});
                    if (localStorage) localStorage.removeItem('USER');
                    resolve(result);
                })
                .catch(reject);
        }),
    init(_, config) {
        if (![
            'userPoolId',
            'userPoolWebClientId',
            'region',
        ].every(opt => Boolean(config[opt]))) {
            throw new Error('userPoolId, userPoolWebClientId and region are required in the config object.');
        }
        
        // Mapeo obligatorio al nuevo formato de configuración unificada de Amplify v6
        Amplify.configure({
            Auth: {
                Cognito: {
                    userPoolId: config.userPoolId,
                    userPoolClientId: config.userPoolWebClientId,
                    loginWith: {
                        username: true,
                    },
                },
            },
        });
    },
    completeNewPassword: ({ commit }, { user, newPassword }) =>
        new Promise((resolve, reject) => {
            // Ajustado para usar el nuevo flujo unificado de confirmación en v6 si aplica
            confirmSignIn({ challengeResponse: newPassword })
                .then((output) => {
                    resolve(output);
                })
                .catch(reject);
        }),
    fetchCredentials: () =>
        new Promise((resolve, reject) => {
            fetchAuthSession()
                .then(session => {
                    resolve(session.credentials);
                }).catch(reject);
        }),
};

export default (store, config, namespace = 'cognito', vuexModuleOptions = {}) => {
    store.registerModule(namespace, {
        namespaced: true,
        state,
        mutations,
        getters,
        actions,
    }, vuexModuleOptions);

    store.dispatch(`${namespace}/init`, config);
};